/**
 * Форматирование значений каталога.
 *
 * Чистые функции без React: их можно проверить отдельно от разметки, и они же
 * используются карточкой списка, каталогом (FE-052) и страницей автомобиля (FE-053).
 *
 * Время хранится в UTC, показывается в Europe/Berlin (CLAUDE.md §1): без явной
 * таймзоны один и тот же `data_observed_at` показывал бы разные даты у
 * пользователей в разных часовых поясах.
 */

import type { FuelType, Transmission, VehicleLocation, VehiclePrice } from './types';

export const DISPLAY_TIME_ZONE = 'Europe/Berlin';

/**
 * Цена из целых центов. `float` для денег запрещён в хранении и расчётах
 * (MVP_TECH_RFC §6) — здесь деление нужно только для показа уже готового числа.
 */
export function formatPrice(price: VehiclePrice, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: price.currency,
      maximumFractionDigits: 0,
    }).format(price.amount_cents / 100);
  } catch {
    // Валюта вне списка Intl: показываем строку, собранную сервером (§21),
    // вместо исключения посреди рендера списка.
    return price.formatted;
  }
}

export function formatMileage(mileageKm: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(mileageKm);
}

/**
 * Город и земля одной строкой. Отсутствующее значение показывается как «Нет данных»,
 * а не пропускается: пустое место читалось бы как «здесь ничего и не должно быть».
 *
 * Источник публикует местоположение не всегда, и подставлять правдоподобный
 * город вместо отсутствующего запрещено (CLAUDE.md §2, §12).
 */
export function formatLocation(location: VehicleLocation, noData: string): string {
  const parts = [location.city, location.state_code].filter(
    (part): part is string => part !== null && part !== '',
  );
  return parts.length > 0 ? parts.join(', ') : noData;
}

/**
 * Год первой регистрации из даты `YYYY-MM-DD`.
 *
 * Строка режется, а не разбирается через `Date`: `new Date('2021-01-01')` — это
 * полночь UTC, и в отрицательных смещениях год «съезжает» на предыдущий.
 */
export function registrationYear(isoDate: string): string | null {
  const match = /^(\d{4})-\d{2}-\d{2}/.exec(isoDate);
  return match?.[1] ?? null;
}

/**
 * Календарная дата `YYYY-MM-DD` → короткая дата локали.
 *
 * Момент берётся в полдень UTC, а не в полночь: снимок рыночной выборки от
 * 1 августа обязан остаться первым августа в любой таймзоне (MVP_PRD §16).
 */
export function formatCalendarDate(isoDate: string, locale: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  return formatObservedDate(`${isoDate}T12:00:00Z`, locale);
}

/** Дата актуальности данных: ISO 8601 UTC → короткая дата в Europe/Berlin. */
export function formatObservedDate(isoDateTime: string, locale: string): string | null {
  const value = new Date(isoDateTime);
  if (Number.isNaN(value.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(value);
}

/**
 * Ключи i18n для справочных значений.
 *
 * Backend отдаёт код (`plug_in_hybrid`), текст живёт во фронтовых словарях —
 * то же правило, что и для объяснений подбора (CLAUDE.md §3 п. 6).
 */
export function fuelLabelKey(fuel: FuelType): string {
  return `catalog.fuel.${fuel}`;
}

export function transmissionLabelKey(transmission: Transmission): string {
  return `catalog.transmission.${transmission}`;
}
